import type { ReactElement } from 'react';
import { ProvisionalMarker } from '../status/index.js';
import { DefinitionList, type Definition } from '../data/index.js';
import { RateCardTable } from './RateCardTable.js';
import { currencyLabel, formatMinorUnits } from '../../lib/format/money.js';
import { formatBasisPoints, formatInterval, humaniseCode } from '../../lib/format/interval.js';
import type { RateCard } from '../../lib/api/queries/equipment.js';

/**
 * P-Q014 — the published rate card.
 *
 * This component displays. It does NOT compute:
 *
 *   · no tax, no commission, no platform fee — OD-6 leaves all three undefined,
 *     so there is no rate to apply and no total that could be called final
 *   · no per-day division of a weekly rate, and no cheapest-rate badge; both
 *     would be a pricing rule the API did not state
 *
 * Every amount arrives as a string of minor units and is rendered in tabular
 * monospace so a column aligns on the decimal (§3). The `ProvisionalMarker`
 * carries OD-6's consequence to the reader rather than leaving it implied.
 */
export interface RateCardViewProps {
  rateCard: RateCard;
}

export function RateCardView({ rateCard }: RateCardViewProps): ReactElement {
  const currency = currencyLabel(rateCard.currency);

  const rates = rateCard.period_rates ?? [];
  const terms: Definition[] = [];
  const push = (term: string, value: string | null): void => {
    if (value !== null && value !== '') terms.push({ term, value });
  };
  push('Minimum rental', formatInterval(rateCard.minimum_rental));
  push('Billing increment', formatInterval(rateCard.billing_increment));
  push('Fuel policy', humaniseCode(rateCard.fuel_policy));
  push('Early return', humaniseCode(rateCard.early_return_policy));
  push('Late grace period', formatInterval(rateCard.late_grace));
  push('Late multiplier', formatBasisPoints(rateCard.late_multiplier_bp));

  return (
    <section className="rate-card" aria-labelledby="rate-card-heading">
      <div className="rate-card__head">
        <h2 id="rate-card-heading" className="section-title">
          Rates
        </h2>
        <span className="rate-card__version numeric">Published version {rateCard.version_no}</span>
      </div>

      {rates.length === 0 ? (
        <p className="rate-card__empty">
          The owner has published this machine without period rates. Ask for terms and they will
          quote you directly.
        </p>
      ) : (
        <RateCardTable rates={rates} currency={rateCard.currency} />
      )}

      {rateCard.overtime_rate_amount_minor === null ? null : (
        <p className="rate-card__overtime">
          Usage beyond the included hours is charged at{' '}
          <span className="numeric">
            {currency} {formatMinorUnits(rateCard.overtime_rate_amount_minor, rateCard.currency)}
          </span>{' '}
          per hour.
        </p>
      )}

      {terms.length === 0 ? null : (
        <>
          <h3 className="subsection-title">Terms</h3>
          <DefinitionList items={terms} label="Rate card terms" />
        </>
      )}

      <p className="rate-card__provisional">
        <ProvisionalMarker
          label="Not a total"
          reason="Taxes and platform fees are not applied yet."
        />
        <span>
          These are the owner’s rates. Taxes and any platform fee are not settled yet, so no figure
          here is a final amount payable.
        </span>
      </p>
    </section>
  );
}
